import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useSearch } from '../contexts/SearchContext'
import { useModal } from '../contexts/ModalContext'
import { useCampaign } from '../contexts/CampaignContext'
import { useEffect, useState } from 'react'
import './TopBar.css'

function TopBar({ onMenuToggle }) {
  const { user, logout, viewAsPlayer, toggleViewAsPlayer } = useAuth()
  const navigate = useNavigate()
  const { openSearch } = useSearch()
  const { confirm } = useModal()
  const { campaign, campaigns, canEdit, realCanEdit } = useCampaign()
  const [switcherOpen, setSwitcherOpen] = useState(false)

  const brandLabel = campaign ? `${campaign.logoEmoji || '🧭'} ${campaign.name}` : '🧭 Start your Quest'

  const renderSwitcher = () => campaigns.length > 1 && (
    <div className="campaign-switcher">
      <button
        className="campaign-switcher-toggle"
        onClick={() => setSwitcherOpen((o) => !o)}
        aria-label="Switch campaign"
      >
        ▾
      </button>
      {switcherOpen && (
        <ul className="campaign-switcher-menu">
          {campaigns.map((c) => (
            <li key={c.id}>
              <button
                className={c.slug === campaign?.slug ? 'active' : ''}
                onClick={() => {
                  setSwitcherOpen(false)
                  navigate(`/c/${c.slug}/campaign`)
                }}
              >
                <span>{c.logoEmoji || '🧭'}</span> {c.name}
              </button>
            </li>
          ))}
          <li className="campaign-switcher-all">
            <button onClick={() => { setSwitcherOpen(false); navigate('/campaigns') }}>All campaigns…</button>
          </li>
        </ul>
      )}
    </div>
  )

  const handleLogout = async () => {
    await confirm({
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      confirmText: 'Logout',
      cancelText: 'Cancel',
      onConfirm: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  const handleCampaignSettingsClick = () => {
    if (campaign) navigate(`/c/${campaign.slug}/settings`)
  }

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        openSearch()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openSearch])

  // Close the campaign switcher dropdown on an outside click or Escape.
  useEffect(() => {
    if (!switcherOpen) return

    const handleOutsideClick = (e) => {
      // Two instances of the switcher exist in the DOM at once (mobile header +
      // desktop top bar), only one of which is visible per breakpoint — a class
      // check via closest() covers both without needing per-instance refs.
      if (!e.target.closest?.('.campaign-switcher')) {
        setSwitcherOpen(false)
      }
    }
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSwitcherOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [switcherOpen])

  return (
    <>
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="sidebar-toggle" onClick={onMenuToggle} aria-label="Toggle sidebar">
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="mobile-logo">
          <span>{brandLabel}</span>
          {renderSwitcher()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {canEdit && (
            <button
              className="icon-button campaign-settings-btn-mobile"
              onClick={handleCampaignSettingsClick}
              title="Campaign settings"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
              }}
            >
              <span>⚙️</span>
            </button>
          )}
          {realCanEdit && (
            <button
              className="icon-button view-toggle-btn-mobile"
              onClick={toggleViewAsPlayer}
              title={viewAsPlayer ? 'Switch back to admin view' : 'Preview as a player'}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
                background: viewAsPlayer ? 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)' : undefined,
                border: viewAsPlayer ? '1px solid #27ae60' : undefined,
                color: viewAsPlayer ? '#ffffff' : undefined
              }}
            >
              <span>{viewAsPlayer ? '🛡️' : '👁️'}</span>
            </button>
          )}
          <button
            className="icon-button logout-btn-mobile"
            onClick={handleLogout}
            title="Logout"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.4rem 0.8rem',
              fontSize: '0.85rem'
            }}
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Desktop Top Bar */}
      <header className="top-bar">
        <div className="top-bar-inner">
          <div className="top-bar-left">
            <div className="logo">{brandLabel}</div>
            {renderSwitcher()}
          </div>
          <div className="top-bar-center">
            <input
              type="text"
              className="search-input"
              placeholder="Search quests, locations, NPCs & factions... (Ctrl+K)"
              onClick={openSearch}
              onFocus={openSearch}
              readOnly
            />
          </div>
          <div className="top-bar-right">
            <button
              className="icon-button user-btn"
              onClick={handleSettingsClick}
              title="User Settings"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <span>👤</span>
              <span>{user?.username} {user?.role === 'admin' && '(Admin)'}</span>
            </button>
            {canEdit && (
              <button
                className="icon-button campaign-settings-btn"
                onClick={handleCampaignSettingsClick}
                title="Campaign settings"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                <span>⚙️</span>
                <span>Campaign Settings</span>
              </button>
            )}
            {realCanEdit && (
              <button
                className="icon-button view-toggle-btn"
                onClick={toggleViewAsPlayer}
                title={viewAsPlayer ? 'Currently previewing as a player — switch back to full admin view' : 'Preview the site as a player would see it'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  background: viewAsPlayer ? 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)' : undefined,
                  border: viewAsPlayer ? '1px solid #27ae60' : undefined,
                  color: viewAsPlayer ? '#ffffff' : undefined
                }}
              >
                <span>{viewAsPlayer ? '🛡️' : '👁️'}</span>
                <span>{viewAsPlayer ? 'Admin View' : 'User View'}</span>
              </button>
            )}
            <button
              className="icon-button logout-btn"
              onClick={handleLogout}
              title="Logout"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 1rem',
                fontSize: '1rem'
              }}
            >
              <span>🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>
    </>
  )
}

export default TopBar
